import { CardActions, Skeleton, styled, Typography } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import Alert from "components/Alert";
import Button from "components/Button";
import Datepicker from "components/Datepicker";
import StyledLink from "components/StyledLink";
import TextField from "components/TextField";
import { createForegroundTracker } from "features/analytics/foregroundTracker";
import { useLogEvent } from "features/analytics/hooks";
import { readSearchReferrer, referrerToProperties } from "features/analytics/searchAttribution";
import { useAuthContext } from "features/auth/AuthProvider";
import { useProfileUser } from "features/profile/hooks/useProfileUser";
import { useLiteUser } from "features/userQueries/useLiteUsers";
import { Trans, useTranslation } from "i18n";
import { GLOBAL, PROFILE } from "i18n/namespaces";
import { useClearablePersistedState } from "platform/usePersistedState";
import React, { RefObject, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { howToWriteRequestGuideUrl } from "routes";
import { service } from "service";
import { CreateHostRequestWrapper } from "service/requests";
import { Temporal } from "temporal-polyfill";
import { theme } from "theme";

const TYPING_GAP_CAP_MS = 3000;

interface FormValuesSnapshot {
  text: string;
  fromDate: Temporal.PlainDate | null;
  toDate: Temporal.PlainDate | null;
}

function isFormField(target: EventTarget | null): boolean {
  if (!target || !(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA";
}

/**
 * Logs `host_request.form_closed` when the form unmounts (closed or submitted),
 * reporting engagement: time open and visible, time a field was focused, active
 * typing time and keystroke count, the final draft length and dates, whether it
 * was submitted, and any search referrer that led the user here.
 *
 * Runs once for the form's open/close lifecycle, so it reads the latest form
 * values and submitted flag from refs rather than re-subscribing on each change.
 */
function useHostRequestFormTracking({
  hostUserId,
  formRef,
  getSubmitted,
  getLatestValues,
}: {
  hostUserId: number;
  formRef: RefObject<HTMLFormElement | null>;
  getSubmitted: () => boolean;
  getLatestValues: () => FormValuesSnapshot;
}) {
  const logEvent = useLogEvent();

  useEffect(() => {
    const tracker = createForegroundTracker();
    let focusAccumMs = 0;
    let focusSince: number | null = null;
    let activeTypingMs = 0;
    let lastKeystroke: number | null = null;
    let keystrokeCount = 0;

    const onFocusIn = (e: FocusEvent) => {
      if (!isFormField(e.target)) return;
      if (focusSince === null) focusSince = performance.now();
    };
    const onFocusOut = (e: FocusEvent) => {
      if (!isFormField(e.target)) return;
      if (focusSince !== null) {
        focusAccumMs += performance.now() - focusSince;
        focusSince = null;
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (!isFormField(e.target)) return;
      const now = performance.now();
      if (lastKeystroke !== null) {
        const gap = now - lastKeystroke;
        if (gap <= TYPING_GAP_CAP_MS) activeTypingMs += gap;
      }
      lastKeystroke = now;
      keystrokeCount += 1;
    };

    document.addEventListener("visibilitychange", tracker.onVisibilityChange);
    const formEl = formRef.current;
    formEl?.addEventListener("focusin", onFocusIn);
    formEl?.addEventListener("focusout", onFocusOut);
    formEl?.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("visibilitychange", tracker.onVisibilityChange);
      formEl?.removeEventListener("focusin", onFocusIn);
      formEl?.removeEventListener("focusout", onFocusOut);
      formEl?.removeEventListener("keydown", onKeyDown);

      if (focusSince !== null) focusAccumMs += performance.now() - focusSince;
      const { foregroundMs, totalMs } = tracker.finalize();

      const { text, fromDate, toDate } = getLatestValues();
      const referrerProps = referrerToProperties(readSearchReferrer(hostUserId));

      logEvent("host_request.form_closed", {
        host_user_id: hostUserId,
        submitted: getSubmitted(),
        form_open_ms: foregroundMs,
        form_open_total_ms: totalMs,
        focus_ms: Math.round(focusAccumMs),
        active_typing_ms: Math.round(activeTypingMs),
        keystroke_count: keystrokeCount,
        text_length: text.length,
        from_date: fromDate?.toString(),
        to_date: toDate?.toString(),
        ...referrerProps,
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

const StyledTitle = styled(Typography)(() => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

const StyledRequestRow = styled("div")(() => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
}));

const StyledDateRow = styled("div")(() => ({
  marginTop: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  width: "72%",
}));

const StyledDatepicker = styled(Datepicker)(() => ({
  marginBottom: theme.spacing(2),
}));

const StyledHelpText = styled(Typography)(() => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const StyledRequestField = styled(TextField)(() => ({
  marginTop: theme.spacing(2),
}));

const StyledSendActions = styled(CardActions)(() => ({
  display: "flex",
  justifyContent: "flex-end",
  marginTop: theme.spacing(2),
}));

const MIN_LENGTH = 250; // Must match backend

// Dates are stored as "yyyy-mm-dd" since Temporal objects don't survive JSON
interface HostRequestDraft {
  text: string;
  fromDate: string | null;
  toDate: string | null;
}

interface NewHostRequestProps {
  setIsRequestSuccess: (value: boolean) => void;
  setIsRequesting: (value: boolean) => void;
}

export default function NewHostRequest({ setIsRequestSuccess, setIsRequesting }: NewHostRequestProps) {
  const { t } = useTranslation([GLOBAL, PROFILE]);
  const user = useProfileUser();
  const currentUserId = useAuthContext().authState.userId;

  // Keep the draft across unmounts (e.g. the mobile profile sheet being swiped closed)
  const [draft, setDraft, clearDraft] = useClearablePersistedState<HostRequestDraft | null>(
    `hostRequestDraft.${currentUserId}.${user.userId}`,
    null,
    "sessionStorage",
  );

  const {
    control,
    getValues,
    handleSubmit,
    register,
    setValue,
    subscribe,
    watch,
    reset,
    formState: { errors },
  } = useForm<CreateHostRequestWrapper>({
    defaultValues: {
      hostUserId: user.userId,
      text: draft?.text ?? "",
      fromDate: draft?.fromDate ? Temporal.PlainDate.from(draft.fromDate) : undefined,
      toDate: draft?.toDate ? Temporal.PlainDate.from(draft.toDate) : undefined,
    },
  });

  useEffect(
    () =>
      subscribe({
        formState: { values: true },
        callback: ({ values: { text, fromDate, toDate } }) => {
          if (!text && !fromDate && !toDate) return clearDraft();
          setDraft({ text, fromDate: fromDate?.toString() ?? null, toDate: toDate?.toString() ?? null });
        },
      }),
    [subscribe, setDraft, clearDraft],
  );

  const textField = watch("text") ?? "";

  const formRef = useRef<HTMLFormElement | null>(null);
  const submittedRef = useRef(false);
  const latestValuesRef = useRef<FormValuesSnapshot>({
    text: "",
    fromDate: null,
    toDate: null,
  });
  latestValuesRef.current = {
    text: watch("text") ?? "",
    fromDate: watch("fromDate") ?? null,
    toDate: watch("toDate") ?? null,
  };

  useHostRequestFormTracking({
    hostUserId: user.userId,
    formRef,
    getSubmitted: () => submittedRef.current,
    getLatestValues: () => latestValuesRef.current,
  });

  const { error, isPending, mutate } = useMutation({
    mutationFn: (data: CreateHostRequestWrapper) => {
      return service.requests.createHostRequest(data);
    },

    onSuccess: () => {
      submittedRef.current = true;
      reset();
      clearDraft();
      setIsRequesting(false);
      setIsRequestSuccess(true);
    },
  });

  const { isLoading: hostLoading, error: hostError } = useLiteUser(user.userId);

  const onSubmit = handleSubmit((data) => {
    // The disabled button doesn't cover submitting with Enter from the date fields
    if (isPending) return;
    mutate(data);
  });

  const hostToday = Temporal.Now.plainDateISO(user.timezone || Temporal.Now.timeZoneId());

  const watchFromDate = watch("fromDate", undefined);
  const arrivalBeforeHostToday = !!watchFromDate && Temporal.PlainDate.compare(watchFromDate, hostToday) < 0;

  // Only react to the arrival date changing: adjusting on every render also fired while the
  // departure year was half-typed (e.g. year 0002), overwriting the departure being entered.
  const pushDepartureAfterArrival = (fromDate: Temporal.PlainDate | null) => {
    const toDate = getValues("toDate");
    if (fromDate && toDate && Temporal.PlainDate.compare(fromDate, toDate) >= 0) {
      setValue("toDate", fromDate.add({ days: 1 }));
    }
  };

  return (
    <>
      <StyledTitle variant="h1">
        {hostLoading ? <Skeleton width="100" /> : t("profile:request_form.send_request", { name: user.name })}
      </StyledTitle>
      {error && <Alert severity="error">{error.message}</Alert>}
      {hostError ? (
        <Alert severity={"error"}>{hostError?.message}</Alert>
      ) : (
        <form onSubmit={onSubmit} ref={formRef}>
          <StyledRequestRow>
            <StyledDateRow>
              <StyledDatepicker
                control={control}
                error={!!errors.fromDate}
                helperText={errors?.fromDate?.message}
                id="from-date"
                label={t("profile:request_form.arrival_date")}
                name="fromDate"
                minValue={hostToday}
                onPostChange={pushDepartureAfterArrival}
                rules={{
                  required: t("profile:request_form.arrival_date_empty"),
                  validate: {
                    notEmpty: (date) => !date || date !== "",
                    notBeforeHostToday: (date) =>
                      !date ||
                      Temporal.PlainDate.compare(Temporal.PlainDate.from(date), hostToday) >= 0 ||
                      t("profile:request_form.arrival_date_before_host_today", {
                        name: user.name,
                      }),
                  },
                }}
              />
              {arrivalBeforeHostToday && (
                <Alert severity="error">
                  {t("profile:request_form.arrival_date_before_host_today", {
                    name: user.name,
                  })}
                </Alert>
              )}
              <StyledDatepicker
                control={control}
                error={!!errors.toDate}
                helperText={errors?.toDate?.message}
                id="to-date"
                label={t("profile:request_form.departure_date")}
                minValue={watchFromDate ? watchFromDate.add({ days: 1 }) : Temporal.Now.plainDateISO()}
                name="toDate"
                rules={{
                  required: t("profile:request_form.departure_date_empty"),
                  validate: {
                    notEmpty: (stringDate) => stringDate !== "",
                    afterArrival: (date) => {
                      const fromDate = getValues("fromDate");
                      return (
                        !date ||
                        !fromDate ||
                        Temporal.PlainDate.compare(Temporal.PlainDate.from(date), fromDate) > 0 ||
                        t("profile:request_form.departure_date_not_after_arrival")
                      );
                    },
                  },
                }}
              />
            </StyledDateRow>
          </StyledRequestRow>
          <StyledHelpText variant="body1">
            <Trans
              i18nKey="profile:request_form.guide_link_help_text"
              components={{
                guideLink: <StyledLink variant="body1" href={howToWriteRequestGuideUrl} />,
              }}
            />
          </StyledHelpText>

          <StyledRequestField
            id="text"
            {...register("text", {
              required: t("profile:request_form.request_description_empty"),
              minLength: {
                value: MIN_LENGTH,
                message: t("profile:request_form.request_chars_remaining", {
                  count: MIN_LENGTH - textField.length,
                }),
              },
            })}
            label={t("profile:request_form.request")}
            minRows={6}
            multiline
            fullWidth
            placeholder={t("profile:request_form.request_description")}
            error={!!errors.text}
            helperText={
              errors.text?.message
                ? errors.text.message
                : MIN_LENGTH - textField.length > 0
                  ? t("profile:request_form.request_chars_remaining", {
                      count: MIN_LENGTH - textField.length,
                    })
                  : ""
            }
          />
          <StyledSendActions>
            <Button onClick={() => setIsRequesting(false)} variant="outlined">
              {t("global:cancel")}
            </Button>
            <Button type="submit" loading={isPending} onClick={onSubmit}>
              {t("global:send")}
            </Button>
          </StyledSendActions>
        </form>
      )}
    </>
  );
}
